/*!
 * Eclectic Chat Widget — TypeScript port
 *
 * A typed port of the standalone `eclectic.js` embed loader. It mounts a chat
 * launcher and exposes a programmatic API:
 *
 *   EclecticWidget.init({ apiKey, apiUrl, ...options })
 *   EclecticWidget.identify({ email, phone, name })
 *   EclecticWidget.open() / .close()
 *   EclecticWidget.show() / .hide()
 *   EclecticWidget.destroy({ clearSession })
 *   EclecticWidget.isOpen()
 *
 * Import it directly for full type-safety:
 *
 *   import EclecticWidget from "@/components/chat/floating/eclectic";
 *
 * The module also assigns `window.EclecticWidget` (client only) so the global
 * API documented for external embeds keeps working.
 *
 * Design notes:
 *  - Nothing happens at import time except defining the API (and the guarded
 *    `window` assignment). All DOM, listeners and network calls start on
 *    init() and are torn down on destroy(), so the widget leaves no trace.
 *  - All mutable runtime state lives in a single `state` object built by
 *    freshState(), which makes reset (identify / destroy) a single assignment.
 *  - `els` is `null` until buildUI() runs. Functions capture the elements they
 *    need into locals right after the null-guard so TypeScript keeps the
 *    non-null narrowing across awaits and nested closures.
 */

import type {
  EclecticConfig,
  EclecticDestroyOptions,
  EclecticEls,
  EclecticInitConfig,
  EclecticServerConfig,
  EclecticState,
  EclecticVisitor,
  EclecticWidgetApi,
  MessagesResponse,
  SessionInitBody,
  SessionInitResponse,
  TrackedListener,
  Translation,
} from "./types";

// ===========================================================================
// Constants
// ===========================================================================
const STORAGE_KEY = "eclectic_session_token";
const POLL_INTERVAL = 1000;
const LOG_PREFIX = "[Eclectic]";

const ICON_CHAT =
  '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M20 2H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h3v3.25a.75.75 0 0 0 1.2.6L13 18h7a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2z"/></svg>';
const ICON_SEND =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 2L11 13"/><path d="M22 2L15 22L11 13L2 9L22 2Z"/></svg>';
const ICON_WHATSAPP =
  '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347ZM12.05 21.785h-.01a9.865 9.865 0 0 1-5.031-1.378l-.361-.214-3.741.981.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884Zm8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/></svg>';

const TRANSLATIONS: Record<string, Translation> = {
  en: {
    headerTitle: "Chat with us",
    minimize: "Minimize",
    inputPlaceholder: "Type a message...",
    formTitle: "Before we start, share your contact info:",
    emailLabel: "Email address",
    emailPlaceholder: "you@example.com",
    phoneLabel: "Or phone number",
    phonePlaceholder: "+1 600 000 000",
    nameLabel: "Name (optional)",
    namePlaceholder: "Your name",
    startChatting: "Start chatting",
    connecting: "Connecting...",
    errorIdentity: "Please provide an email or phone number.",
    errorConnection: "Connection error. Please try again.",
    errorSend: "Failed to send. Please try again.",
    loadPrevious: "Load previous conversation",
  },
  es: {
    headerTitle: "Chatea con nosotros",
    minimize: "Minimizar",
    inputPlaceholder: "Escribe un mensaje...",
    formTitle: "Antes de empezar, comparte tu información de contacto:",
    emailLabel: "Correo electrónico",
    emailPlaceholder: "tu@ejemplo.com",
    phoneLabel: "O número de teléfono",
    phonePlaceholder: "+34 600 000 000",
    nameLabel: "Nombre (opcional)",
    namePlaceholder: "Tu nombre",
    startChatting: "Comenzar a chatear",
    connecting: "Conectando...",
    errorIdentity:
      "Por favor, proporciona un correo electrónico o número de teléfono.",
    errorConnection: "Error de conexión. Por favor, inténtalo de nuevo.",
    errorSend: "Error al enviar. Por favor, inténtalo de nuevo.",
    loadPrevious: "Cargar conversación anterior",
  },
};

const STYLES = `
    * { box-sizing: border-box; margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; }

    .ecl-launcher { position: fixed; bottom: 20px; right: 20px; display: flex; flex-direction: column; align-items: center; gap: 8px; z-index: 999999; }
    .ecl-launcher.left { right: auto; left: 20px; }
    .ecl-launcher-buttons { display: flex; align-items: center; gap: 10px; }

    .ecl-bubble { width: 48px; height: 48px; border-radius: 50%; background: var(--ecl-primary, #0058e8); color: white; border: none; cursor: pointer; box-shadow: 2px 2px 8px 0px rgba(38, 38, 38, 0.20); display: flex; align-items: center; justify-content: center; transition: transform 0.2s ease-out; }
    .ecl-bubble:hover { transform: scale(1.1); }
    .ecl-bubble:active { transform: scale(0.9); }
    .ecl-bubble svg { width: 24px; height: 24px; fill: currentColor; }

    .ecl-whatsapp-link { width: 48px; height: 48px; border-radius: 50%; background: #25d366; color: white; display: none; align-items: center; justify-content: center; box-shadow: 0 4px 12px rgba(0,0,0,0.15); text-decoration: none; transition: transform 0.2s, box-shadow 0.2s; }
    .ecl-launcher.open .ecl-whatsapp-link.ecl-visible { display: flex; animation: ecl-wa-pop-in 0.35s cubic-bezier(0.34, 1.56, 0.64, 1) both; }
    @keyframes ecl-wa-pop-in { from { opacity: 0; transform: scale(0); } to { opacity: 1; transform: scale(1); } }
    .ecl-whatsapp-link:hover { transform: scale(1.08); box-shadow: 0 6px 20px rgba(0,0,0,0.2); }
    .ecl-whatsapp-link svg { width: 22px; height: 22px; fill: currentColor; }

    .ecl-container { position: fixed; bottom: 84px; right: 20px; width: 420px; height: 640px; max-height: calc(100vh - 110px); border-radius: 16px; background: #fff; box-shadow: 0 8px 30px rgba(0,0,0,0.12); display: flex; flex-direction: column; overflow: hidden; z-index: 999998; opacity: 0; visibility: hidden; pointer-events: none; transform: translateY(18px) scale(0.96); transform-origin: bottom right; transition: opacity 0.24s ease, transform 0.28s cubic-bezier(0.2, 0.8, 0.2, 1), visibility 0s linear 0.28s; }
    .ecl-container.open { opacity: 1; visibility: visible; pointer-events: auto; transform: translateY(0) scale(1); transition: opacity 0.2s ease, transform 0.32s cubic-bezier(0.2, 0.8, 0.2, 1), visibility 0s linear 0s; }
    .ecl-container.left { right: auto; left: 20px; transform-origin: bottom left; }

    .ecl-header { padding: 14px 18px; background: var(--ecl-primary, #0058e8); color: white; font-size: 13px; font-weight: 600; display: flex; align-items: center; justify-content: space-between; }
    .ecl-header-close { background: none; border: none; color: white; cursor: pointer; width: 28px; height: 28px; padding: 0; display: inline-flex; align-items: center; justify-content: center; }
    .ecl-header-close-line { display: block; width: 14px; height: 2px; border-radius: 999px; background: currentColor; }

    .ecl-messages { flex: 1; overflow-y: auto; padding: 16px; display: flex; flex-direction: column; gap: 8px; }
    .ecl-msg { max-width: 80%; padding: 8px 12px; border-radius: 16px; font-size: 12px; line-height: 1.45; word-wrap: break-word; white-space: pre-wrap; }
    .ecl-msg.visitor { align-self: flex-end; background: var(--ecl-primary, #0058e8); color: white; border-bottom-right-radius: 4px; }
    .ecl-msg.business { align-self: flex-start; background: #f1f3f5; color: #1a1a1a; border-bottom-left-radius: 4px; }
    .ecl-msg a { color: inherit; text-decoration: underline; word-break: break-all; }
    .ecl-msg a:hover { opacity: 0.8; }

    .ecl-input-area { padding: 12px 16px; border-top: 1px solid #e5e7eb; display: flex; gap: 8px; }
    .ecl-chat-brand { padding: 0 16px 12px; display: flex; justify-content: center; }
    .ecl-chat-brand-link { color: #334155; font-size: 12px; font-weight: 600; line-height: 1; text-decoration: none; }
    .ecl-chat-brand-link:hover { text-decoration: underline; }
    .ecl-input { flex: 1; border: 1px solid #d1d5db; border-radius: 20px; padding: 8px 14px; font-size: 12px; outline: none; resize: none; }
    .ecl-input:focus { border-color: var(--ecl-primary, #0058e8); }
    .ecl-load-history-btn { align-self: center; background: none; border: 1px solid var(--ecl-primary, #0058e8); color: var(--ecl-primary, #0058e8); border-radius: 12px; padding: 6px 14px; font-size: 12px; cursor: pointer; margin-bottom: 4px; }
    .ecl-load-history-btn:hover { background: var(--ecl-primary, #0058e8); color: white; }
    .ecl-send-btn { background: var(--ecl-primary, #0058e8); color: white; border: none; border-radius: 50%; width: 40px; height: 40px; cursor: pointer; display: flex; align-items: center; justify-content: center; }
    .ecl-send-btn:disabled { opacity: 0.5; cursor: not-allowed; }
    .ecl-send-btn svg { width: 18px; height: 18px; }

    .ecl-identity-form { padding: 16px 20px; display: flex; flex-direction: column; gap: 8px; overflow-y: auto; }
    .ecl-identity-form .ecl-form-title { font-size: 13px; font-weight: 600; color: #1a1a1a; margin-bottom: 4px; }
    .ecl-identity-form label { font-size: 11px; color: #6b7280; font-weight: 500; }
    .ecl-identity-form input { border: 1px solid #d1d5db; border-radius: 8px; padding: 7px 10px; font-size: 12px; outline: none; }
    .ecl-identity-form input:focus { border-color: var(--ecl-primary, #0058e8); }
    .ecl-identity-form button { background: var(--ecl-primary, #0058e8); color: white; border: none; border-radius: 8px; padding: 8px; font-size: 12px; font-weight: 600; cursor: pointer; margin-top: 4px; }
    .ecl-identity-form .ecl-error { color: #ef4444; font-size: 12px; }

    .ecl-typing { align-self: flex-start; display: flex; align-items: center; padding: 10px 14px; background: #f1f3f5; border-radius: 16px; border-bottom-left-radius: 4px; }
    .ecl-typing-spinner { width: 18px; height: 18px; border: 2px solid #dbeafe; border-top-color: #3b82f6; border-radius: 50%; animation: ecl-spin 0.7s linear infinite; }
    @keyframes ecl-spin { to { transform: rotate(360deg); } }

    .ecl-tooltip { position: fixed; bottom: 56px; right: 84px; background: #1a1a1a; color: #fff; font-size: 12px; line-height: 1.4; padding: 10px 14px; border-radius: 10px; max-width: 210px; box-shadow: 0 4px 14px rgba(0,0,0,0.18); z-index: 999999; animation: ecl-tooltip-in 0.4s ease-out; pointer-events: none; }
    .ecl-tooltip.left { right: auto; left: 84px; }
    .ecl-tooltip::after { content: ''; position: absolute; top: 50%; right: -6px; transform: translateY(-50%); width: 0; height: 0; border-top: 6px solid transparent; border-bottom: 6px solid transparent; border-left: 6px solid #1a1a1a; }
    .ecl-tooltip.left::after { right: auto; left: -6px; border-left: none; border-right: 6px solid #1a1a1a; }
    @keyframes ecl-tooltip-in { from { opacity: 0; transform: translateX(8px); } to { opacity: 1; transform: translateX(0); } }

    @media (max-width: 480px) {
      .ecl-launcher.open { bottom: calc(96px + env(safe-area-inset-bottom)); }
      .ecl-launcher.open .ecl-bubble { display: none; }
      .ecl-container { width: 100vw; height: 100vh; bottom: 0; right: 0; border-radius: 0; }
      .ecl-container.left { left: 0; }
    }
  `;

// ===========================================================================
// State
//
// `config`       – resolved options from init()
// `widgetConfig` – business config fetched from the API (colors, name, ...)
// `state`        – all mutable runtime flags; rebuilt by freshState()
// `els`          – references to DOM nodes inside the shadow root (null until built)
// `listeners`    – every global listener we attach, so destroy() can remove all
// ===========================================================================
let config: EclecticConfig = freshConfig();
let widgetConfig: EclecticServerConfig = {};
let state: EclecticState = freshState();
let els: EclecticEls | null = null;
const listeners: TrackedListener[] = [];
let historyPatched = false;
let origPushState: typeof history.pushState | null = null;
let origReplaceState: typeof history.replaceState | null = null;

function freshConfig(): EclecticConfig {
  return {
    apiKey: "",
    apiUrl: "",
    visitorEmail: "",
    visitorPhone: "",
    visitorName: "",
    tooltipMessage: "",
    locale: "",
    excludeUrls: [],
  };
}

function freshState(): EclecticState {
  return {
    sessionToken: null,
    conversationId: null,
    isOpen: false,
    isVisible: true,
    isDestroyed: false,
    identityCollected: false,
    identityFormShown: false,
    waitingForReply: false,
    isPollInFlight: false,
    pollIntervalId: null,
    welcomePreviewShown: false,
    hasAutoOpened: false,
    lastMessageTimestamp: null,
    tooltipDismissTimer: null,
  };
}

// ===========================================================================
// Small utilities
// ===========================================================================
function detectLocale(): string {
  const seg = window.location.pathname.split("/")[1];
  const code = config.locale || (seg && seg.length === 2 ? seg : null) || "es";
  return code.toLowerCase();
}

function t(): Translation {
  return TRANSLATIONS[detectLocale()] || TRANSLATIONS.en;
}

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

// Guard used by every public method: returns false (and warns) if the widget
// isn't currently mounted, so a stray API call can't throw.
function ready(): boolean {
  if (!els || state.isDestroyed) {
    console.warn(
      `${LOG_PREFIX} Not initialised. Call EclecticWidget.init(config) first.`,
    );
    return false;
  }
  return true;
}

// Tracked event binding so destroy() can unbind everything.
function on(target: EventTarget, type: string, handler: EventListener): void {
  target.addEventListener(type, handler);
  listeners.push({ target, type, handler });
}

function removeAllListeners(): void {
  while (listeners.length) {
    const entry = listeners.pop();
    if (entry) entry.target.removeEventListener(entry.type, entry.handler);
  }
}

// ===========================================================================
// URL exclusion + visibility
// ===========================================================================
function matchesExcludePattern(pattern: string, pathname: string): boolean {
  const norm = pathname.length > 1 ? pathname.replace(/\/$/, "") : pathname;
  const regexStr = pattern
    .replace(/[.+^${}()|[\]\\]/g, "\\$&")
    .replace(/\/\*\*/g, "\x00") // ** → any depth (incl. zero segments)
    .replace(/\*/g, "[^/]+") // *  → exactly one segment
    .replace(/\x00/g, "(?:/.*)?");
  return new RegExp("^" + regexStr + "$").test(norm);
}

function isExcludedUrl(): boolean {
  const patterns = config.excludeUrls || [];
  return patterns.some((p) => matchesExcludePattern(p, window.location.pathname));
}

function refreshHostVisibility(): void {
  const e = els;
  if (!e) return;
  const hidden = state.isDestroyed || !state.isVisible || isExcludedUrl();
  e.host.style.display = hidden ? "none" : "";
}

// ===========================================================================
// API layer
// ===========================================================================
function apiHeaders(includeSession: boolean): Record<string, string> {
  const h: Record<string, string> = {
    "Content-Type": "application/json",
    "X-API-Key": config.apiKey,
  };
  if (includeSession && state.sessionToken) {
    h["X-Session-Token"] = state.sessionToken;
  }
  return h;
}

async function apiPost<T>(
  path: string,
  body: unknown,
  includeSession: boolean,
): Promise<T> {
  const res = await fetch(`${config.apiUrl}${path}`, {
    method: "POST",
    headers: apiHeaders(includeSession),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Request failed: ${res.status}`);
  }
  return res.json();
}

async function apiGet<T>(path: string): Promise<T> {
  const res = await fetch(`${config.apiUrl}${path}`, {
    headers: apiHeaders(true),
  });
  if (!res.ok) throw new Error(`Request failed: ${res.status}`);
  return res.json();
}

// ===========================================================================
// Session
// ===========================================================================
async function initSession(
  email: string | null,
  phone: string | null,
  name: string | null,
): Promise<SessionInitResponse> {
  const body: SessionInitBody = {
    metadata: {
      page_url: window.location.href,
      user_agent: navigator.userAgent,
      referrer: document.referrer,
    },
  };
  if (state.sessionToken) body.existing_session_token = state.sessionToken;
  if (email) body.visitor_email = email;
  if (phone) body.visitor_phone = phone;
  if (name) body.visitor_name = name;

  const data = await apiPost<SessionInitResponse>(
    "/api/web-embed/session/init/",
    body,
    false,
  );
  if (data.session_token) {
    state.sessionToken = data.session_token;
    localStorage.setItem(STORAGE_KEY, data.session_token);
  }
  state.conversationId = data.conversation_id ?? null;
  widgetConfig = data.config || widgetConfig;
  return data;
}

function sendMessageRequest(text: string): Promise<unknown> {
  return apiPost<unknown>("/api/web-embed/messages/send/", { text }, true);
}

async function fetchConfig(): Promise<"ok" | "invalid_key" | "error"> {
  try {
    const res = await fetch(`${config.apiUrl}/api/web-embed/widget/config/`, {
      headers: {
        "Content-Type": "application/json",
        "X-API-Key": config.apiKey,
      },
    });
    if (!res.ok) {
      return res.status === 401 || res.status === 403 ? "invalid_key" : "error";
    }
    widgetConfig = await res.json();
    applyWidgetConfig();
    return "ok";
  } catch {
    return "error";
  }
}

// ===========================================================================
// Polling
// ===========================================================================
async function pollMessages(): Promise<void> {
  if (state.isPollInFlight) return;
  state.isPollInFlight = true;
  try {
    const since = state.lastMessageTimestamp || new Date(0).toISOString();
    const data = await apiGet<MessagesResponse>(
      `/api/web-embed/messages/poll/?since=${encodeURIComponent(since)}`,
    );
    if (data.messages && data.messages.length) {
      let gotReply = false;
      data.messages.forEach((m) => {
        if (!m.is_from_visitor) {
          appendMessage(m.text, false);
          gotReply = true;
        }
      });
      state.lastMessageTimestamp = data.messages[data.messages.length - 1].created_at;
      if (gotReply && state.waitingForReply) {
        state.waitingForReply = false;
        hideTypingIndicator();
        stopPolling();
      }
    }
  } catch (err) {
    console.warn(`${LOG_PREFIX} Poll error:`, errorMessage(err));
  } finally {
    state.isPollInFlight = false;
  }
}

function startPolling(): void {
  stopPolling();
  state.pollIntervalId = setInterval(pollMessages, POLL_INTERVAL);
}

function stopPolling(): void {
  if (state.pollIntervalId !== null) {
    clearInterval(state.pollIntervalId);
    state.pollIntervalId = null;
  }
}

async function loadHistory(): Promise<void> {
  try {
    const data = await apiGet<MessagesResponse>(
      "/api/web-embed/messages/history/?limit=10&offset=0",
    );
    if (data.messages) {
      data.messages.forEach((m) => appendMessage(m.text, m.is_from_visitor));
      if (data.messages.length) {
        state.lastMessageTimestamp = data.messages[data.messages.length - 1].created_at;
      }
    }
  } catch (err) {
    console.warn(`${LOG_PREFIX} History load error:`, errorMessage(err));
  }
}

// ===========================================================================
// Messages rendering
// ===========================================================================
function linkify(text: string): string {
  const escaped = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  return escaped
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(
      /(https?:\/\/[^\s<]+)/g,
      '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>',
    );
}

function appendMessage(text: string, isVisitor: boolean): void {
  const e = els;
  if (!e || !e.messages) return;
  const messages = e.messages;
  const msg = document.createElement("div");
  msg.className = `ecl-msg ${isVisitor ? "visitor" : "business"}`;
  msg.innerHTML = linkify(text);
  messages.appendChild(msg);
  messages.scrollTop = messages.scrollHeight;
}

function showTypingIndicator(): void {
  const e = els;
  if (!e || e.typing || !e.messages) return;
  const messages = e.messages;
  const typing = document.createElement("div");
  typing.className = "ecl-typing";
  typing.innerHTML = '<div class="ecl-typing-spinner"></div>';
  messages.appendChild(typing);
  messages.scrollTop = messages.scrollHeight;
  e.typing = typing;
}

function hideTypingIndicator(): void {
  const e = els;
  if (e && e.typing) {
    e.typing.remove();
    e.typing = null;
  }
}

// ===========================================================================
// Tooltip
// ===========================================================================
function tooltipMessage(): string {
  return config.tooltipMessage || widgetConfig.tooltip_message || "";
}

function showTooltip(message: string, autoDismiss: boolean): void {
  const e = els;
  if (!message || !e || e.tooltip) return;
  const tooltip = document.createElement("div");
  tooltip.className =
    "ecl-tooltip" + (widgetConfig.position === "bottom-left" ? " left" : "");
  tooltip.textContent = message;
  e.shadow.appendChild(tooltip);
  e.tooltip = tooltip;
  if (autoDismiss) state.tooltipDismissTimer = setTimeout(dismissTooltip, 5000);
}

function dismissTooltip(): void {
  if (state.tooltipDismissTimer) {
    clearTimeout(state.tooltipDismissTimer);
    state.tooltipDismissTimer = null;
  }
  const e = els;
  if (e && e.tooltip) {
    e.tooltip.remove();
    e.tooltip = null;
  }
}

// ===========================================================================
// Config application
// ===========================================================================
function applyWidgetConfig(): void {
  const e = els;
  if (!e || !e.headerTitle) return;
  const { host, launcher, container, headerTitle } = e;
  headerTitle.textContent = widgetConfig.display_name || t().headerTitle;
  if (widgetConfig.primary_color) {
    host.style.setProperty("--ecl-primary", widgetConfig.primary_color);
  }
  updateWhatsAppButton();
  const isLeft = widgetConfig.position === "bottom-left";
  launcher.classList.toggle("left", isLeft);
  container.classList.toggle("left", isLeft);
  if (e.tooltip) e.tooltip.classList.toggle("left", isLeft);
}

function applyTranslations(): void {
  const e = els;
  if (!e || !e.headerTitle || !e.closeBtn || !e.input) return;
  const { headerTitle, closeBtn, input } = e;
  const tr = t();
  if (!widgetConfig.display_name) headerTitle.textContent = tr.headerTitle;
  closeBtn.setAttribute("aria-label", tr.minimize);
  input.placeholder = tr.inputPlaceholder;
}

function updateWhatsAppButton(): void {
  const e = els;
  if (!e) return;
  const { whatsApp } = e;
  const normalized = String(widgetConfig.whatsapp_number || "").replace(
    /[^\d]/g,
    "",
  );
  const hasNumber = normalized.length > 0;
  whatsApp.classList.toggle("ecl-visible", hasNumber);
  if (hasNumber) whatsApp.href = `https://wa.me/${normalized}`;
  else whatsApp.removeAttribute("href");
}

// ===========================================================================
// Identity gating
// ===========================================================================
function shouldDeferIdentityUntilInput(): boolean {
  return Boolean(
    widgetConfig.open_on_load &&
      widgetConfig.welcome_message &&
      !state.identityCollected &&
      !state.sessionToken &&
      !config.visitorEmail &&
      !config.visitorPhone,
  );
}

function showWelcomePreviewIfNeeded(): void {
  if (
    state.welcomePreviewShown ||
    state.identityCollected ||
    !widgetConfig.welcome_message
  ) {
    return;
  }
  appendMessage(widgetConfig.welcome_message, false);
  state.welcomePreviewShown = true;
}

// Build the identity form with DOM APIs (visitor values go through .value,
// never string-interpolated into innerHTML).
function showIdentityForm(): void {
  const e = els;
  if (
    state.identityFormShown ||
    !e ||
    !e.inputArea ||
    !e.messages ||
    !e.container
  ) {
    return;
  }
  const { inputArea, messages, container } = e;
  state.identityFormShown = true;
  const tr = t();

  const form = document.createElement("div");
  form.className = "ecl-identity-form";

  const title = document.createElement("div");
  title.className = "ecl-form-title";
  title.textContent = tr.formTitle;
  form.appendChild(title);

  const field = (
    labelText: string,
    type: string,
    value: string,
    placeholder: string,
  ): HTMLInputElement => {
    const label = document.createElement("label");
    label.textContent = labelText;
    const input = document.createElement("input");
    input.type = type;
    input.placeholder = placeholder;
    input.value = value || "";
    form.appendChild(label);
    form.appendChild(input);
    return input;
  };

  const emailInput = field(
    tr.emailLabel,
    "email",
    config.visitorEmail,
    tr.emailPlaceholder,
  );
  const phoneInput = field(
    tr.phoneLabel,
    "tel",
    config.visitorPhone,
    tr.phonePlaceholder,
  );
  const nameInput = field(
    tr.nameLabel,
    "text",
    config.visitorName,
    tr.namePlaceholder,
  );

  const errorEl = document.createElement("div");
  errorEl.className = "ecl-error";
  errorEl.style.display = "none";
  form.appendChild(errorEl);

  const submitBtn = document.createElement("button");
  submitBtn.textContent = tr.startChatting;
  form.appendChild(submitBtn);

  inputArea.style.display = "none";
  messages.style.display = "none";
  container.insertBefore(form, inputArea);

  submitBtn.addEventListener("click", async () => {
    const email = emailInput.value.trim();
    const phone = phoneInput.value.trim();
    const name = nameInput.value.trim();
    if (!email && !phone) {
      errorEl.textContent = tr.errorIdentity;
      errorEl.style.display = "block";
      return;
    }
    submitBtn.disabled = true;
    submitBtn.textContent = tr.connecting;
    try {
      const data = await initSession(email, phone, name);
      state.identityCollected = true;
      state.identityFormShown = false;
      form.remove();
      inputArea.style.display = "flex";
      messages.style.display = "flex";
      onSessionReady(data);
    } catch {
      errorEl.textContent = tr.errorConnection;
      errorEl.style.display = "block";
      submitBtn.disabled = false;
      submitBtn.textContent = tr.startChatting;
    }
  });
}

// ===========================================================================
// Chat send flow
// ===========================================================================
async function handleSend(): Promise<void> {
  const e = els;
  if (!e || !e.input || !e.sendBtn) return;
  const { input, sendBtn } = e;
  const text = input.value.trim();
  if (!text) return;
  if (!state.identityCollected) {
    showIdentityForm();
    return;
  }

  input.value = "";
  sendBtn.disabled = true;
  appendMessage(text, true);
  showTypingIndicator();

  try {
    await sendMessageRequest(text);
    state.waitingForReply = true;
    startPolling();
    setTimeout(pollMessages, 500);
  } catch {
    hideTypingIndicator();
    appendMessage(t().errorSend, false);
  }
  sendBtn.disabled = !input.value.trim();
  input.focus();
}

// ===========================================================================
// "Load previous conversation" button
// ===========================================================================
async function showLoadHistoryButtonIfNeeded(): Promise<void> {
  const e = els;
  if (!e || !e.messages) return;
  const messages = e.messages;
  try {
    const peek = await apiGet<MessagesResponse>(
      "/api/web-embed/messages/history/?limit=1&offset=0",
    );
    const totalCount = peek.total_count ?? 0;
    if (totalCount <= 1) return;

    const preExistingCount = totalCount;
    const domCountAtShow = messages.querySelectorAll(".ecl-msg").length;

    const btn = document.createElement("button");
    btn.className = "ecl-load-history-btn";
    btn.textContent = t().loadPrevious;
    messages.insertBefore(btn, messages.firstChild);

    btn.addEventListener("click", async () => {
      btn.remove();
      try {
        const domCountNow = messages.querySelectorAll(".ecl-msg").length;
        const offset = domCountNow - domCountAtShow;
        const limit = Math.min(preExistingCount, 10);
        if (limit <= 0) return;

        const data = await apiGet<MessagesResponse>(
          `/api/web-embed/messages/history/?limit=${limit}&offset=${offset}`,
        );
        if (data.messages && data.messages.length) {
          const fragment = document.createDocumentFragment();
          data.messages.forEach((m) => {
            const el = document.createElement("div");
            el.className = `ecl-msg ${m.is_from_visitor ? "visitor" : "business"}`;
            el.innerHTML = linkify(m.text);
            fragment.appendChild(el);
          });
          messages.insertBefore(fragment, messages.firstChild);
          messages.scrollTop = messages.scrollHeight;
        }
      } catch (err) {
        console.warn(`${LOG_PREFIX} History load error:`, errorMessage(err));
      }
    });
  } catch {
    /* don't block the session flow */
  }
}

// ===========================================================================
// Open / close
// ===========================================================================
function openPanel(focusInput: boolean): void {
  const e = els;
  if (!e || !e.container || !e.launcher || !e.messages || !e.input) return;
  const { container, launcher, messages, input } = e;
  state.isOpen = true;
  container.classList.add("open");
  launcher.classList.add("open");
  dismissTooltip();
  if (!state.identityCollected) {
    if (shouldDeferIdentityUntilInput()) showWelcomePreviewIfNeeded();
    else showIdentityForm();
  }
  if (focusInput !== false && state.identityCollected) input.focus();
  messages.scrollTop = messages.scrollHeight;
}

function closePanel(): void {
  const e = els;
  if (!e || !e.container || !e.launcher) return;
  state.isOpen = false;
  e.container.classList.remove("open");
  e.launcher.classList.remove("open");
}

function maybeAutoOpen(): void {
  if (
    state.hasAutoOpened ||
    !widgetConfig.open_on_load ||
    isExcludedUrl() ||
    config.visitorEmail ||
    config.visitorPhone ||
    state.sessionToken
  ) {
    return;
  }
  state.hasAutoOpened = true;
  setTimeout(() => openPanel(false), 250);
}

// ===========================================================================
// Session-ready callback
// ===========================================================================
function onSessionReady(data: SessionInitResponse): void {
  applyWidgetConfig();
  if (!data.is_new_session) {
    loadHistory();
    return;
  }
  if (widgetConfig.welcome_message && !state.welcomePreviewShown) {
    appendMessage(widgetConfig.welcome_message, false);
    // Set the timestamp before polling so the server-stored welcome message
    // isn't fetched a second time on the first poll.
    state.lastMessageTimestamp = new Date().toISOString();
  } else if (state.welcomePreviewShown) {
    state.lastMessageTimestamp = new Date().toISOString();
  }
  showLoadHistoryButtonIfNeeded();
}

// ===========================================================================
// SPA navigation awareness
// ===========================================================================
function onUrlChange(): void {
  applyTranslations();
  refreshHostVisibility();
}

function patchHistory(): void {
  if (historyPatched) return;
  historyPatched = true;
  origPushState = history.pushState;
  origReplaceState = history.replaceState;
  const push = origPushState.bind(history);
  const replace = origReplaceState.bind(history);
  history.pushState = (...args: Parameters<History["pushState"]>) => {
    const r = push(...args);
    onUrlChange();
    return r;
  };
  history.replaceState = (...args: Parameters<History["replaceState"]>) => {
    const r = replace(...args);
    onUrlChange();
    return r;
  };
}

function unpatchHistory(): void {
  if (!historyPatched) return;
  if (origPushState) history.pushState = origPushState;
  if (origReplaceState) history.replaceState = origReplaceState;
  historyPatched = false;
}

// ===========================================================================
// Build the Shadow DOM UI
// ===========================================================================
function buildUI(): void {
  const host = document.createElement("div");
  host.id = "eclectic-chat-widget-host";
  host.style.display = "none";
  document.body.appendChild(host);
  const shadow = host.attachShadow({ mode: "closed" });

  const style = document.createElement("style");
  style.textContent = STYLES;
  shadow.appendChild(style);

  const launcher = document.createElement("div");
  launcher.className = "ecl-launcher";
  shadow.appendChild(launcher);

  const launcherButtons = document.createElement("div");
  launcherButtons.className = "ecl-launcher-buttons";
  launcher.appendChild(launcherButtons);

  const whatsApp = document.createElement("a");
  whatsApp.className = "ecl-whatsapp-link";
  whatsApp.target = "_blank";
  whatsApp.rel = "noopener noreferrer";
  whatsApp.setAttribute("aria-label", "Open WhatsApp");
  whatsApp.innerHTML = ICON_WHATSAPP;
  launcherButtons.appendChild(whatsApp);

  const bubble = document.createElement("button");
  bubble.className = "ecl-bubble";
  bubble.innerHTML = ICON_CHAT;
  launcherButtons.appendChild(bubble);

  const container = document.createElement("div");
  container.className = "ecl-container";
  container.innerHTML = `
      <div class="ecl-header">
        <span class="ecl-header-title"></span>
        <button class="ecl-header-close" aria-label="">
          <span class="ecl-header-close-line" aria-hidden="true"></span>
        </button>
      </div>
      <div class="ecl-messages"></div>
      <div class="ecl-input-area">
        <input class="ecl-input" type="text" />
        <button class="ecl-send-btn" disabled>${ICON_SEND}</button>
      </div>
      <div class="ecl-chat-brand">
        <a class="ecl-chat-brand-link" href="https://try-eclectic.com">Eclectic AI</a>
      </div>
    `;
  shadow.appendChild(container);

  els = {
    host,
    shadow,
    launcher,
    whatsApp,
    bubble,
    container,
    headerTitle: container.querySelector<HTMLSpanElement>(".ecl-header-title"),
    closeBtn: container.querySelector<HTMLButtonElement>(".ecl-header-close"),
    messages: container.querySelector<HTMLDivElement>(".ecl-messages"),
    input: container.querySelector<HTMLInputElement>(".ecl-input"),
    sendBtn: container.querySelector<HTMLButtonElement>(".ecl-send-btn"),
    inputArea: container.querySelector<HTMLDivElement>(".ecl-input-area"),
    typing: null,
    tooltip: null,
  };

  bindUIEvents();
}

// Listeners scoped to UI nodes: these live and die with the shadow root, so
// they don't need to be in the global `listeners` registry.
function bindUIEvents(): void {
  const e = els;
  if (!e || !e.closeBtn || !e.input || !e.sendBtn) return;
  const { bubble, closeBtn, input, sendBtn } = e;

  bubble.addEventListener("click", () =>
    state.isOpen ? closePanel() : openPanel(true),
  );
  closeBtn.addEventListener("click", closePanel);
  bubble.addEventListener("mouseenter", () => {
    if (!state.isOpen) showTooltip(tooltipMessage(), false);
  });
  bubble.addEventListener("mouseleave", dismissTooltip);
  bubble.addEventListener("focus", () => {
    if (!state.isOpen) showTooltip(tooltipMessage(), false);
  });
  bubble.addEventListener("blur", dismissTooltip);

  input.addEventListener("keydown", (ev) => {
    if (ev.key === "Enter" && !ev.shiftKey) {
      ev.preventDefault();
      void handleSend();
    }
  });
  input.addEventListener("input", () => {
    sendBtn.disabled = !input.value.trim();
  });
  const maybeDeferredForm = () => {
    if (!state.identityCollected && shouldDeferIdentityUntilInput()) {
      showIdentityForm();
    }
  };
  input.addEventListener("focus", maybeDeferredForm);
  input.addEventListener("click", maybeDeferredForm);
  sendBtn.addEventListener("click", () => void handleSend());
}

// Global listeners that DO need cleanup on destroy().
function attachGlobalListeners(): void {
  on(document, "visibilitychange", () => {
    if (document.hidden) stopPolling();
    else if (
      state.sessionToken &&
      state.conversationId &&
      state.waitingForReply
    ) {
      startPolling();
      pollMessages();
    }
  });
  on(window, "popstate", onUrlChange);
  patchHistory();
}

// ===========================================================================
// Boot sequence (run once per init)
// ===========================================================================
async function boot(): Promise<void> {
  state.sessionToken = localStorage.getItem(STORAGE_KEY);

  const result = await fetchConfig();
  if (result === "invalid_key") {
    console.error(`${LOG_PREFIX} Invalid API key — widget removed.`);
    teardown(false);
    return;
  }

  refreshHostVisibility();

  if (config.visitorEmail || config.visitorPhone) {
    await resumeOrStartSession(
      () =>
        initSession(config.visitorEmail, config.visitorPhone, config.visitorName),
      false,
    );
  } else if (state.sessionToken) {
    await resumeOrStartSession(() => initSession(null, null, null), true);
  } else {
    maybeAutoOpen();
  }
}

async function resumeOrStartSession(
  startFn: () => Promise<SessionInitResponse>,
  isResume: boolean,
): Promise<void> {
  try {
    const data = await startFn();
    state.identityCollected = true;
    onSessionReady(data);
  } catch (err) {
    if (isResume) {
      // Token expired/invalid — drop it and fall back to anonymous flow.
      localStorage.removeItem(STORAGE_KEY);
      state.sessionToken = null;
      state.identityCollected = false;
      maybeAutoOpen();
    } else {
      console.error(`${LOG_PREFIX} Session init failed:`, errorMessage(err));
    }
  }
}

// Shared teardown used by destroy() and by the invalid-key path.
function teardown(clearSession: boolean): void {
  stopPolling();
  dismissTooltip();
  removeAllListeners();
  unpatchHistory();

  if (clearSession) localStorage.removeItem(STORAGE_KEY);
  const e = els;
  if (e && e.host.parentNode) e.host.remove();

  config = freshConfig();
  widgetConfig = {};
  state = freshState();
  els = null;
}

// ===========================================================================
// Public API
// ===========================================================================
function init(cfg: EclecticInitConfig): void {
  if (els) {
    console.warn(
      `${LOG_PREFIX} Already initialised. Use identify() to change visitor, or destroy() then init().`,
    );
    return;
  }
  if (!cfg || !cfg.apiKey || !cfg.apiUrl) {
    console.error(`${LOG_PREFIX} init() requires { apiKey, apiUrl }.`);
    return;
  }
  config = {
    apiKey: cfg.apiKey,
    apiUrl: String(cfg.apiUrl)
      .replace(/\/$/, "")
      .replace(/\/api$/, ""),
    visitorEmail: cfg.visitorEmail || "",
    visitorPhone: cfg.visitorPhone || "",
    visitorName: cfg.visitorName || "",
    tooltipMessage: cfg.tooltipMessage || "",
    locale: cfg.locale || "",
    excludeUrls: Array.isArray(cfg.excludeUrls) ? cfg.excludeUrls : [],
  };
  state = freshState();
  buildUI();
  attachGlobalListeners();
  void boot();
}

async function identify(visitor: EclecticVisitor): Promise<void> {
  if (!ready()) return;
  const e = els;
  if (!e || !e.messages || !e.inputArea) return;
  const { shadow, messages, inputArea } = e;
  if (!visitor || (!visitor.email && !visitor.phone)) {
    console.warn(`${LOG_PREFIX} identify() needs at least { email } or { phone }.`);
    return;
  }

  // Drop the current session so initSession() starts clean.
  stopPolling();
  localStorage.removeItem(STORAGE_KEY);
  Object.assign(state, {
    sessionToken: null,
    conversationId: null,
    identityCollected: false,
    identityFormShown: false,
    waitingForReply: false,
    welcomePreviewShown: false,
    lastMessageTimestamp: null,
  });
  messages.innerHTML = "";

  config.visitorEmail = visitor.email || "";
  config.visitorPhone = visitor.phone || "";
  config.visitorName = visitor.name || "";

  const existingForm = shadow.querySelector(".ecl-identity-form");
  if (existingForm) {
    existingForm.remove();
    inputArea.style.display = "flex";
    messages.style.display = "flex";
  }

  try {
    const data = await initSession(
      config.visitorEmail,
      config.visitorPhone,
      config.visitorName,
    );
    state.identityCollected = true;
    onSessionReady(data);
  } catch (err) {
    console.error(`${LOG_PREFIX} identify() failed:`, errorMessage(err));
  }
}

function open(): void {
  if (ready()) openPanel(true);
}

function close(): void {
  if (ready()) closePanel();
}

function show(): void {
  if (ready()) {
    state.isVisible = true;
    refreshHostVisibility();
  }
}

function hide(): void {
  if (ready()) {
    state.isVisible = false;
    closePanel();
    refreshHostVisibility();
  }
}

function destroy(options?: EclecticDestroyOptions): void {
  if (!els) return;
  teardown(!options || options.clearSession !== false);
}

function isOpen(): boolean {
  return state.isOpen;
}

const EclecticWidget: EclecticWidgetApi = Object.freeze({
  init,
  identify,
  open,
  close,
  show,
  hide,
  destroy,
  isOpen,
});

// Expose the global API for external/embed usage. Guarded so the module is
// safe to import during server-side static rendering, where `window` is absent.
if (typeof window !== "undefined" && !window.EclecticWidget) {
  window.EclecticWidget = EclecticWidget;
}

export default EclecticWidget;
export { EclecticWidget };
export type {
  EclecticInitConfig,
  EclecticVisitor,
  EclecticDestroyOptions,
  EclecticWidgetApi,
} from "./types";
